/** @jsxImportSource @emotion/react */
import type React from 'react';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import { VerificationCodeInput } from '../../../components/verification-code-input/verification-code-input';
import {
  CodeGlowWrap,
  CountdownLabel,
  Email,
  EmailWrapper,
  FullWidth,
  Headline,
  Link,
  PrimaryButton,
  Screen,
  StatusLabel,
  Subhead,
} from './verify-reset-code.styled';
import { useVerifyResetCode } from './verify-reset-code.hooks';
import { VSpace } from '../../chat/settings/account/account-settings';
import type { LoginStepProps } from '../login-shell.types';
import { SkipButton } from '../../registration/pages/secure-your-account/secure-your-account.styled';

const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

export const VerifyResetCode: React.FC<LoginStepProps> = ({ back, go, data, setData }) => {
  const email = data.email || '';

  const {
    code,
    setCode,
    verifying,
    verifyStatus,
    error,
    canContinue,
    handleVerify,
    resending,
    secondsLeft,
    handleResend,
  } = useVerifyResetCode({
    email,
    onVerified: (verifiedCode) => {
      setData({ resetCode: verifiedCode });
      go('reset-password');
    },
  });

  const handleGoBack = () => {
    if (verifying) return;
    back();
  };

  const handleEditEmail = () => {
    go('forgot-password');
  };

  // Allow pasting a full 6-digit code
  const handlePasteCode = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = e.clipboardData.getData('text') || '';
    const digits = digitsOnly(pasted).slice(0, 6);
    if (!digits) return;
    e.preventDefault();
    setCode(digits);
  };

  return (
    <Screen data-testid="verify-reset-code-screen">
      <div>
        <VSpace $h={32} />

        <Headline>Reset code sent.</Headline>

        <VSpace $h={16} />

        <Subhead>We sent a secure verification code to</Subhead>

        <VSpace $h={4} />
        <EmailWrapper>
          <Email>{email}</Email>
          <img
            src="./edit-email-icon.svg"
            alt="Edit email"
            style={{ cursor: 'pointer' }}
            onClick={handleEditEmail}
          />
        </EmailWrapper>
      </div>
      
      <FullWidth>
        {/* 6-digit code input */}
        <CodeGlowWrap status={verifyStatus} onPaste={handlePasteCode}>
          <VerificationCodeInput length={6} value={code} onChange={setCode} status={verifyStatus} />
        </CodeGlowWrap>

        <VSpace $h={8} />
        {/* <FullWidth> */}
        {verifyStatus === 'success' && <StatusLabel status="success">Code verified</StatusLabel>}
        {verifyStatus === 'error' && error && <StatusLabel status="error">{error}</StatusLabel>}

        <VSpace $h={16} />

        <PrimaryButton
          disabled={!canContinue}
          onClick={handleVerify}
          data-testid="verify-reset-code-continue"
        >
          {verifying ? <UniversalSpinner /> : 'Continue'}
        </PrimaryButton>
        <VSpace $h={28} />

        <>
          {secondsLeft > 0 ? (
            <CountdownLabel>
              Didn't receive a code?{' '}
              <Link>Resend again ({secondsLeft.toString().padStart(2, '0')}s)</Link>
            </CountdownLabel>
          ) : (
            <CountdownLabel>
              Didn’t receive a code?{' '}
              <Link onClick={handleResend}>{resending ? 'Sending...' : 'Resend'}</Link>
            </CountdownLabel>
          )}
        </>
        <VSpace $h={36} />

        <SkipButton
          onClick={handleGoBack}
          data-testid="verify-reset-code-back"
          css={{
            color: '#B7BDCB',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: '100%',
            letterSpacing: '0px',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'pointer',
            height: 'auto',
          }}
        >
          Go back
        </SkipButton>
      </FullWidth>
    </Screen>
  );
};
